import { ERROR_MESSAGES } from './events.config';

export type DebounceCallback<T = any> = (...args: T[]) => void;

/**
 * @name debounce
 *
 * @description Debounce events or anything else that needs to be debounced so
 * that they run only once only after X time passed since the last call of the debounce
 *
 * @param {number} timmer
 * @param {DebounceCallback} callback
 *
 * @returns return a debounce function which executes the callback when the
 * function is not called again and 'timer' seconds passed
 */
export function debounce(timer: number, callback: DebounceCallback, shouldRunOnFirstCall: boolean) {
  if (typeof callback !== 'function') {
    throw new Error(ERROR_MESSAGES.callbackNeedToBeFunction);
  }

  let timeout = 0;

  /**
   * Since this is a function generator, there might be times
   * where i want different behaviors for the different
   * function calls. Hence the @immediate variable which
   * let's us decide on function call if we want the debouncer to run
   * immediately before debouncing the next calls
   */
  return function(...args: unknown[]) {
    const later = () => {
      timeout = 0;
      if (!shouldRunOnFirstCall) {
        callback(...args);
      }
    };

    const callNow = shouldRunOnFirstCall && !timeout;

    window.clearTimeout(timeout);

    timeout = window.setTimeout(later, timer);

    if (callNow) callback(...args);
  };
}
